/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, Text, ActionButton, CheckIconButton } from '@components/atoms'
import { UserList, SideModal, Tabs, Tab } from '@components/molecules'
import { AccessGroupEditor, AccessGroupList } from '@components/systems'
import { mono, boFont } from '@components/systems/backoffice/backoffice.tokens'

import {
	useAppSelector,
	useAppDispatch,
	SecetionUserListType,
	accessControlActions,
	ApiGetDataStatus,
	AccessControlStateType,
	CrudType,
} from '@redux/index'
import { emptyGroup } from '@redux/store/admin/accessControl.store'
import { UserType, AccessGroupType } from '@schemas'
import { produce } from 'immer'

/** 그룹 편집 사이드 패널 — 탭(이름 변경 · 멤버 관리) 또는 가입 그룹 목록 */
export const AccessControlSideModal = () => {
	// redux dispatch
	const reduxDispatch = useAppDispatch()

	// redux state
	const crud: CrudType = useAppSelector(state => state.auth.currentAccess)
	const leftModalInfo: AccessControlStateType['leftModalInfo'] = useAppSelector(
		state => state.accessControl.leftModalInfo,
	)
	const groupList: AccessGroupType[] = useAppSelector(
		state => state.accessControl.groupList,
	)
	const userList: UserType[] = useAppSelector(state => state.accessControl.userList)
	const groupListStatus: ApiGetDataStatus = useAppSelector(
		state => state.accessControl.groupListStatus,
	)
	const userListStatus: ApiGetDataStatus = useAppSelector(
		state => state.accessControl.userListStatus,
	)
	const userSectionList: SecetionUserListType[] = useAppSelector(
		state => state.accessControl.userSectionList,
	)
	const selectedAccessUser: UserType = useAppSelector(
		state => state.accessControl.selectedAccessUser,
	)
	const selectedAccessGroup: AccessGroupType = useAppSelector(
		state => state.accessControl.selectedAccessGroup,
	)
	const showSideModal: boolean = useAppSelector(
		state => state.accessControl.showSideModal,
	)
	const leftModalTabIndex: number = useAppSelector(
		state => state.accessControl.leftModalTabIndex,
	)
	const groupListByUserTab: AccessGroupType[] = useAppSelector(
		state => state.accessControl.groupListByUserTab,
	)

	// state
	const [openedAddUserCateg, setOpenedAddUserCateg] = React.useState<string[]>([])
	const [selectedAddUserIds, setSelectedAddUserIds] = React.useState<string[]>([])
	const [isChanged, setIsChanged] = React.useState<boolean>(false)

	// variables
	const title: string =
		leftModalInfo.target === 'groupEditor' ? selectedAccessGroup.name : '그룹 리스트'

	React.useEffect(() => {
		const newSelectedUserIds = selectedAccessGroup.users.map(aUser => aUser.id)
		setSelectedAddUserIds(newSelectedUserIds)
	}, [selectedAccessGroup])

	React.useEffect(() => {
		const prevUserIds: string[] = selectedAccessGroup['users'].map(aUser => aUser.id)
		const isNotChangedForPrev: boolean = prevUserIds.every(aPrevId =>
			selectedAddUserIds.includes(aPrevId),
		)
		const isNotChangedForNew: boolean = selectedAddUserIds.every(aNewId =>
			prevUserIds.includes(aNewId),
		)

		setIsChanged(!(isNotChangedForPrev && isNotChangedForNew))
	}, [selectedAddUserIds, selectedAccessGroup['users']])

	const leftModalTabList = React.useMemo(
		() => [
			{
				index: 0,
				id: 'title',
				label: '이름 변경',
			},
			{
				index: 1,
				id: 'member',
				label: '그룹 멤버 관리',
			},
		],
		[],
	)

	const onTabIndexChange = React.useCallback(
		(e: React.SyntheticEvent, newIndex: number) => {
			reduxDispatch(accessControlActions.LEFT_MODAL_TAB_INDEX(newIndex))
		},
		[],
	)

	const onGroupDelete = React.useCallback(() => {
		reduxDispatch(accessControlActions.DELETE_GROUP(selectedAccessGroup.id))
		reduxDispatch(accessControlActions.SELECTED_ACCESS_GROUP(emptyGroup))
		reduxDispatch(accessControlActions.SHOW_SIDE_MODAL(false))
	}, [selectedAccessGroup, emptyGroup])

	const onAddUserSave = React.useCallback(() => {
		reduxDispatch(
			accessControlActions.UPDATE_ACCESS_GROUP_USERS({
				accessGroupId: selectedAccessGroup.id,
				userIds: selectedAddUserIds,
			}),
		)
	}, [selectedAddUserIds])

	const onUserClick = (aUser: UserType) => {
		const newUserIds = [...selectedAddUserIds]
		const index = newUserIds.indexOf(aUser.id)
		if (index > -1) {
			// delete
			newUserIds.splice(index, 1)
		} else {
			// add
			newUserIds.push(aUser.id)
		}

		setSelectedAddUserIds(newUserIds)
	}

	const onAddUserCollapsOpen = (id: string) => {
		const newOpenedAddUserCateg: string[] = produce(openedAddUserCateg, draft => {
			const idx: number = openedAddUserCateg.indexOf(id)
			if (idx === -1) {
				draft.push(id)
			} else {
				draft.splice(idx, 1)
			}
		})

		setOpenedAddUserCateg(newOpenedAddUserCateg)
	}

	const onGroupClick = (aGroup: AccessGroupType) => {
		const thisGroup = groupList.filter(aGroup2 => aGroup2.id === aGroup.id)[0]
		const newUserId = selectedAccessUser.id
		const newUserIds = thisGroup.users.map(aUser => aUser.id)
		const index = thisGroup.users.findIndex(aUser => aUser.id === newUserId)

		if (index > -1) {
			// delete
			newUserIds.splice(index, 1)
		} else {
			// add
			newUserIds.push(newUserId)
		}

		reduxDispatch(
			accessControlActions.UPDATE_ACCESS_GROUP_USERS({
				accessGroupId: thisGroup.id,
				userIds: newUserIds,
			}),
		)
	}

	return (
		<View css={style}>
			<SideModal
				show={showSideModal}
				title={selectedAccessGroup.name}
				renderTitle={
					<Text as="span" className="sideModalTitle" i18n={selectedAccessGroup.i18n}>
						{title}
					</Text>
				}
				onCloseModal={() => reduxDispatch(accessControlActions.SHOW_SIDE_MODAL(false))}
			>
				<View className="accessControlSideModalContent">
					{leftModalInfo.target === 'groupEditor' ? (
						<>
							<View className="leftModlaTabList">
								<Tabs
									value={leftModalTabIndex}
									onChange={onTabIndexChange}
									aria-label="그룹 편집 탭"
								>
									{leftModalTabList.map(aTab => (
										<Tab key={aTab.id} label={aTab.label} className="tabItem" />
									))}
								</Tabs>
							</View>

							{leftModalTabIndex === 0 ? (
								<View>
									<AccessGroupEditor show={showSideModal} onDelete={onGroupDelete} />
								</View>
							) : leftModalTabIndex === 1 ? (
								<View className="accessGroupAddUserListOuter">
									<View className="accessGroupAddUserListView">
										<UserList
											status={userListStatus}
											userList={userList}
											onUserClick={
												crud.includes('U')
													? (aUser: UserType) => onUserClick(aUser)
													: undefined
											}
											openedUserCateg={openedAddUserCateg}
											onCollapsOpen={onAddUserCollapsOpen}
											selectedUserIds={selectedAddUserIds}
										/>
									</View>

									<View className="accessGroupAddUserListBottomView">
										<span className="accessGroupAddedUserCount">{`${selectedAddUserIds.length}명 선택됨`}</span>

										{crud.includes('U') && (
											<View className="addButtonView">
												<ActionButton
													title="저장"
													tooltip="저장"
													style={{ margin: 0 }}
													variant="contained"
													buttonColor="primary"
													onClick={onAddUserSave}
													iconName="check"
													disabled={!isChanged}
												/>
											</View>
										)}
									</View>
								</View>
							) : null}
						</>
					) : (
						<View className="joinGroupListView">
							<AccessGroupList
								groupList={groupList}
								status={groupListStatus}
								onGroupClick={onGroupClick}
								selectedGroupIds={groupListByUserTab.map(aGroup => aGroup.id)}
								rightAccessory={(group, isChecked) => {
									return (
										<CheckIconButton
											checked={isChecked}
											activeColor={mono.ink}
											css={{ marginRight: '1rem' }}
											onClick={() => onGroupClick(group)}
										/>
									)
								}}
							/>
						</View>
					)}
				</View>
			</SideModal>
		</View>
	)
}

const style = css({
	fontFamily: boFont.ui,

	'.sideModalTitle': {
		fontFamily: boFont.ui,
		fontSize: 14,
		fontWeight: 500,
		color: mono.ink,
	},

	'.accessControlSideModalContent': {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',

		'.leftModlaTabList': {
			flexShrink: 0,
			padding: '0 12px',
			borderBottom: `1px solid ${mono.line}`,

			'.MuiTabs-root': {
				minHeight: 40,
				height: 40,

				'.MuiTab-root': {
					minHeight: 40,
					height: 40,
					padding: '0 8px',
					minWidth: 0,
					marginRight: 12,
				},
			},
		},

		'.accessGroupAddUserListOuter': {
			position: 'relative',
			display: 'flex',
			flexDirection: 'column',
			flex: 1,
			minHeight: 0,

			'.accessGroupAddUserListView': {
				flex: 1,
				minHeight: 0,
				padding: '8px 12px',
				overflow: 'auto',
			},
		},
	},

	'.accessGroupAddUserListBottomView': {
		flexShrink: 0,
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		width: '100%',
		height: 56,
		padding: '0 12px 0 20px',
		background: mono.surface,
		borderTop: `1px solid ${mono.line}`,

		'.accessGroupAddedUserCount': {
			fontSize: 12,
			fontWeight: 300,
			color: mono.dim,
			fontVariantNumeric: 'tabular-nums',
		},
	},

	'.joinGroupListView': {
		width: '100%',
		height: '100%',
	},
})
